import { useEffect, useMemo, useState } from 'react';
import { useCriticalInfoDocument } from '@/hooks/house/critical-info/useCriticalInfoDocument';
import { createImportantQuery } from '@/components/dashbaord/assistant/queryUtil';
import { AssistantQuery } from '@/components/dashbaord/assistant/types';

/**
 * Hook for generating important queries based on important information
 *
 * Creates "Where is X" queries for each system in the important information document
 */
export const useImportantQueries = (houseId: number) => {
	const [data, setData] = useState<AssistantQuery[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const { systems, isLoading, error: criticalInfoError, hasCriticalInfo } = useCriticalInfoDocument(houseId);

	useEffect(() => {
		if (isLoading) {
			setLoading(true);
			return;
		}

		if (criticalInfoError) {
			setError('Failed to load important information');
			setLoading(false);
			return;
		}

		try {
			// Generate queries for each system in the important information document
			const queries = systems.map((system) => createImportantQuery(system.name));
			setData(queries);
			setLoading(false);
		} catch (error) {
			console.error('Error generating important queries:', error);
			setError('Failed to load important information');
			setLoading(false);
		}
	}, [systems, isLoading, criticalInfoError]);

	return useMemo(
		() => ({
			data,
			isLoading: loading,
			error,
			hasCriticalInfo,
		}),
		[data, error, hasCriticalInfo, loading],
	);
};

export default useImportantQueries;
